import React, { useState, lazy, Suspense, useEffect, useMemo } from "react";
import AssetTap from "./AssetTap";
import { useGetCategoryQuery } from "../../slices/assetApiSlice";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// Mapping API category names to corresponding component filenames
// This maps all possible API category names to their component files
const categoryMapping = {
  // Primary categories (direct mapping)
  Building: "Building",
  Landscaping: "Landscaping",
  Machinery: "Machinery",
  Other: "Other",
  Room: "Room",
  // "Room QR": "Room", // This was commented out, ensuring it's not active
  // RoomQR: "Room",    // This was commented out, ensuring it's not active

  // Alternative names that might come from API
  Infrastructure: "Landscaping",
  Facility: "Landscaping",
  Furniture: "Other",
  Equipment: "Other",

  // Case-insensitive fallbacks (will be handled in the component logic)
};

// Default and static components
const DefaultCategory = lazy(() => import("./AssetCategories/Other"));

const MAsset = () => {
  const { data: categories, error, isLoading, refetch } = useGetCategoryQuery();
  const [activeTab, setActiveTab] = useState(null);
  const [tabLoading, setTabLoading] = useState(false);

  // 1. Dynamically find all files in the AssetCategories folder
  const categoryModules = import.meta.glob("./AssetCategories/*.jsx");

  // 2. Convert those file paths into a list of category names (memoized to prevent infinite loops)
  const folderCategories = useMemo(() => {
    return Object.keys(categoryModules).map((path) => {
      // Extracts "Building" from "./AssetCategories/Building.jsx"
      return path.split("/").pop().replace(".jsx", "");
    });
  }, [categoryModules]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading assets...", {
        description: "Please wait while we fetch the asset categories.",
        id: "loading-assets",
      });
    } else {
      toast.dismiss("loading-assets");

      if (error) {
        toast.error("Load Failed", {
          description:
            "Unable to load asset categories. Please try again later.",
        });
      }

      if (categories && !activeTab) {
        console.log("🔍 DEBUG: Raw categories from API:", categories);
        const filtered = categories.filter(
          (category) => category.deleted != true,
        );

        // Find the first available tab from folders or API
        const defaultTab =
          folderCategories.length > 0
            ? folderCategories[0]
            : filtered.length > 0
              ? filtered[0].name
              : "Room";
        setActiveTab(defaultTab);
      }
    }
  }, [isLoading, error, categories, folderCategories, activeTab]);

  // 3. Build the category list dynamically
  // Priority: Database categories (if they exist) + folder-based fallback + Room QR
  const getCategoryList = () => {
    const list = [];

    // First, add all active categories from the database
    if (categories && categories.length > 0) {
      const activeDbCategories = categories.filter((cat) => !cat.deleted);
      activeDbCategories.forEach((apiCat) => {
        list.push({ name: apiCat.name });
      });
    }

    // Then add folder-based categories that aren't already in the list
    folderCategories.forEach((folderCat) => {
      if (!list.find((c) => c.name === folderCat)) {
        list.push({ name: folderCat });
      }
    });


    return list;
  };

  const categoryList = getCategoryList();

  console.log("Dynamic Category List:", categoryList);
  console.log("Folder Categories:", folderCategories);
  console.log("Database Categories:", categories);

  // Wait until activeTab is ready
  if (isLoading || !activeTab) return null;
  if (error) return null;

  // Determine component to load with better logic
  const normalizedTab = activeTab.replace(/\s+/g, "").toLowerCase();

  // Get the component file name from the mapping
  let componentFile = categoryMapping[activeTab];

  // If not found in mapping, try case-insensitive matching
  if (!componentFile) {
    const lowerTab = activeTab.toLowerCase();
    const foundKey = Object.keys(categoryMapping).find(
      (key) => key.toLowerCase() === lowerTab,
    );
    componentFile = foundKey ? categoryMapping[foundKey] : "Other";
  }

  const CategoryComponent = lazy(() =>
    /* @vite-ignore */
    import(`./AssetCategories/${componentFile}.jsx`).catch((error) => {
      console.error(`Failed to load ${componentFile}.jsx:`, error);
      return DefaultCategory;
    }),
  );

  console.log("Loading component:", {
    activeTab,
    normalizedTab,
    componentFile,
  });

  const handleTabChange = (tab) => {
    setTabLoading(true);
    setActiveTab(tab);
    // The loading state will be handled by Suspense
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold tracking-tight">
            Assets Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and organize assets across different categories
          </p>
        </div>

        {/* Tabs */}
        <div className="px-6 pb-2">
          <AssetTap
            activeTab={activeTab}
            setActiveTab={handleTabChange}
            categories={categoryList}
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {categoryList.length > 0 ? (
            <Suspense
              fallback={
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                  <p className="text-muted-foreground">Loading content...</p>
                </div>
              }
            >
              <CategoryComponent
                key={activeTab}
                category={activeTab}
                onLoad={() => setTabLoading(false)}
              />
            </Suspense>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground">No categories available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MAsset;
